import { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LookApi from '../../api/LookApi';
import { LookDto } from '../../api/types/LookDtos';
import { CartItemsContext } from '../../context/CartItemsContext';
import { CartItemsContextType } from '../../context/types/CartItemsContextType';
import { SelectedGarmentContext } from '../../context/SelectedGarmentContext';
import { SelectedGarmentContextType } from '../../context/types/SelectedGarmentContentType';
import { GarmentsHorizontalScrollContext } from '../../context/GarmentsHorizontalScrollContext';
import { GarmentsHorizontalScrollContextType } from '../../context/types/GarmentsHorizontalScrollContextType';
import { SelectedGarmentSizeContext } from '../../context/SelectedGarmentSizeContext';
import { SelectedGarmentSizeContextType } from '../../context/types/SelectedGarmentSizeContextType';
import { FiltersContext } from '../../context/FiltersContext';
import { FiltersContextType } from '../../context/types/FiltersContextType';
import {
  MIN_LOOKS_LEFT_TO_LOAD_NEW,
  MIN_TABLET_BODY_WIDTH,
} from '../../constants/StylistConstants';
import { TextRegular } from '../Text';
import { BuyLookButton } from '../Button';
import Look from '../Look';

const Looks = () => {
  const CONCAT_LOOKS: boolean = true;
  const RELOAD_LOOKS: boolean = false;

  const [looks, setLooks] = useState<LookDto[]>([]);
  const [current, setCurrent] = useState(0);
  const [pageCursor, setPageCursor] = useState('');
  const [lookPrice, setLookPrice] = useState(0);
  const { updateCartItems } = useContext(
    CartItemsContext,
  ) as CartItemsContextType;
  const { saveSelectedGarmentId } = useContext(
    SelectedGarmentContext,
  ) as SelectedGarmentContextType;
  const { saveScrollSize } = useContext(
    GarmentsHorizontalScrollContext,
  ) as GarmentsHorizontalScrollContextType;
  const { resetSelectedGarmentSizeId } = useContext(
    SelectedGarmentSizeContext,
  ) as SelectedGarmentSizeContextType;
  const { filterGarmentsIds, filterColors, filterGarmentsCount } = useContext(
    FiltersContext,
  ) as FiltersContextType;

  const mobileDisplay: boolean = window.innerWidth < MIN_TABLET_BODY_WIDTH;

  const loadLooks = (concatLooks: boolean, cursor: string) => {
    (async () => {
      const response = await LookApi.getLooks(
        cursor,
        filterGarmentsIds,
        filterColors,
        filterGarmentsCount,
      );
      if (response.data) {
        setLooks(
          concatLooks ? looks.concat(response.data.looks) : response.data.looks,
        );
        setPageCursor(response.data.pageCursor);
      }
    })();
  };

  useEffect(() => {
    setCurrent(0);
    saveScrollSize(0);
    resetSelectedGarmentSizeId();
    saveSelectedGarmentId(0);
    loadLooks(RELOAD_LOOKS, '');
    // eslint-disable-next-line
  }, [filterGarmentsIds, filterColors, filterGarmentsCount]);

  useEffect(() => {
    setLookPrice(
      looks[current]?.look.reduce((sum, garment) => sum + +garment.price, 0),
    );
  }, [looks, current]);

  let length: number = looks?.length;

  const nextLook = () => {
    setCurrent(current === length - 1 ? 0 : current + 1);
    setLookPrice(
      looks[current].look.reduce((sum, garment) => sum + +garment.price, 0),
    );
    if (looks.length - current <= MIN_LOOKS_LEFT_TO_LOAD_NEW) {
      loadLooks(CONCAT_LOOKS, pageCursor);
      length = looks.length;
    }
    saveSelectedGarmentId(0);
    saveScrollSize(0);
    resetSelectedGarmentSizeId();
  };

  const prevLook = () => {
    if (current !== 0) {
      setCurrent(current - 1);
      setLookPrice(
        looks[current].look.reduce((sum, garment) => sum + +garment.price, 0),
      );
    }
    saveSelectedGarmentId(0);
    saveScrollSize(0);
    resetSelectedGarmentSizeId();
  };

  const buyLook = () => {
    updateCartItems(looks[current].look.length);
  };

  return looks[current]?.look ? (
    <Container>
      <NavBar>
        <Left>
          <PreviousLooks onClick={() => prevLook()}>
            <ArrowBackIcon fontSize={'small'} />
            {!mobileDisplay && (
              <TextRegular fontWeight={600}>previews</TextRegular>
            )}
          </PreviousLooks>
          <NextLook onClick={() => nextLook()}>
            {!mobileDisplay && <TextRegular fontWeight={600}>next</TextRegular>}
            <ArrowForwardIcon fontSize={'small'} />
          </NextLook>
        </Left>
        <Right>
          <BuyLookButton onClick={() => buyLook()}>
            Buy look &nbsp;&nbsp;${lookPrice}
          </BuyLookButton>
        </Right>
      </NavBar>
      <Look look={looks[current].look} />
    </Container>
  ) : (
    <></>
  );
};

const Container = styled.div`
  width: 100%;
`;

const NavBar = styled.div`
  padding: 15px 0;
  display: flex;

  @media (max-width: ${MIN_TABLET_BODY_WIDTH - 1}px) {
    padding: 10px 0;
  }
`;

const Left = styled.div`
  flex: 1;
  display: flex;
`;

const PreviousLooks = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 20px;
  cursor: pointer;
`;

const NextLook = styled.div`
  margin-left: 55px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 20px;
  cursor: pointer;

  @media (max-width: ${MIN_TABLET_BODY_WIDTH - 1}px) {
    margin-left: 35px;
  }
`;

const Right = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-end;
`;

export default Looks;
